/* eslint-disable @next/next/no-img-element */
'use client'
import { LoginAction, RequestOtpAction } from '@/actions'
import { ButtonRoundStyle, CheckboxCT, Form, RHFTextField } from '@/components'
import { CALLBACK_URL_KEY } from '@/constants'
import Link from '@/features/ref/components/Link'
import { setRef } from '@/features/ref/utils/setRef'
import { useAccountStore, useCartStore, useCompareStore } from '@/store'
import { DEFAULT_LOGIN_REDIRECT } from '@/utils/routes'
import yup from '@/utils/yup-global'
import { loginSchema, otpRequestSchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, Stack, styled, Tab, TabProps, Tabs, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

type Props = {
  onOtpSuccess?: (phone: string) => void
  goToRegisterPage?: () => void
  goToForgotPasswordPage?: () => void
}

const TabStyle = styled(Tab)<TabProps>(({ theme }) => ({
  width: '50%',
  fontWeight: 600,
  padding: 8,
  height: 38,
  minHeight: 38,
}))

export const LoginForm = ({ onOtpSuccess, goToRegisterPage, goToForgotPasswordPage }: Props) => {
  const t = useTranslations('UserProfile')
  const searchParams = useSearchParams()

  const [value, setValue] = useState<'OTP' | 'SYSTEM'>('OTP')

  const handleChange = (event: React.SyntheticEvent, newValue: 'OTP' | 'SYSTEM') => {
    setValue(newValue)
  }
  return (
    <>
      <Tabs
        value={value}
        defaultValue={'SYSTEM'}
        onChange={handleChange}
        centered
        sx={{
          '& .MuiTab-root': { textTransform: 'none' },
          minHeight: 38,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <TabStyle label="Đăng nhập bằng OTP" value={'OTP'} />
        <TabStyle label="Tài khoản hệ thống" value={'SYSTEM'} />
      </Tabs>
      {value === 'SYSTEM' && <SystemLoginForm goToForgotPasswordPage={goToForgotPasswordPage} />}
      {value === 'OTP' && (
        <OTPLoginForm onOtpSuccess={onOtpSuccess} goToForgotPasswordPage={goToForgotPasswordPage} />
      )}
      <Typography variant="body2">
        {t('Nếu đây là lần đầu tiên bạn đến với chúng tôi')}{' '}
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '1em',
            '& a': {
              color: 'inherit',
              textDecoration: 'none',
              transition: 'all 0.3s',
              '&:hover': {
                textDecoration: 'underline',
                opacity: 0.8,
              },
            },
          }}
        >
          <Link
            href={
              '/register?' +
              CALLBACK_URL_KEY +
              '=' +
              encodeURIComponent(searchParams.get(CALLBACK_URL_KEY) || '')
            }
            onNavigate={(e) => {
              if (goToRegisterPage) {
                e.preventDefault()
                goToRegisterPage()
              }
            }}
          >
            {t('Đăng ký ngay')}
          </Link>
        </Typography>
      </Typography>
    </>
  )
}

const SystemLoginForm = ({ goToForgotPasswordPage }: Props) => {
  const [isPending, startTransition] = useTransition()
  const t = useTranslations('UserProfile')
  const { setUser } = useAccountStore()
  const method = useForm<yup.InferType<typeof loginSchema>>({ resolver: yupResolver(loginSchema) })
  const { handleSubmit } = method
  const { mergeCart } = useCartStore()
  const { assignToUser } = useCompareStore()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get(CALLBACK_URL_KEY)
  const onSubmit = async (values: yup.InferType<typeof loginSchema>) => {
    startTransition(async () => {
      try {
        // Thực hiện đăng nhập
        const loginResult = await LoginAction(values, callbackUrl)

        if (loginResult?.status === 'error') {
          toast.error(loginResult?.message || t('Đăng nhập thất bại'))
          return
        }

        // Cập nhật thông tin user và merge data
        await setUser(loginResult?.data ?? {})
        await mergeCart()
        await assignToUser()
        let redirectUrl = callbackUrl ?? DEFAULT_LOGIN_REDIRECT
        const refValue = loginResult?.data?.increment_id
        const redirectUrlWithRef = setRef(refValue, redirectUrl)
        window.location.href = redirectUrlWithRef
        // router.push(redirectUrlWithRef)
      } catch (error) {
        console.error('Login error:', error)
        toast.error(t('Có lỗi xảy ra trong quá trình đăng nhập'))
      }
    })
  }
  return (
    <Form onSubmit={handleSubmit(onSubmit)} methods={method}>
      <Stack spacing={1.5}>
        <RHFTextField
          autoFocus
          name="email"
          type="email"
          label={t('E-mail Số điện thoại')}
          placeholder={t('Nhập email hoặc SĐT của bạn')}
          disabled={isPending}
        />
        <RHFTextField
          type="password"
          name="password"
          label={t('Mật khẩu')}
          disabled={isPending}
          placeholder={t('Nhập mật khẩu của bạn')}
        />
      </Stack>
      <Grid container spacing={1} mt={1}>
        <Grid size={12}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <CheckboxCT name="rememberMe" label={t('Duy trì đăng nhập')} disabled={isPending} />
            <Box
              sx={{
                textAlign: { xs: 'start', sm: 'end' },
                fontSize: '0.875rem',
                '& a': {
                  textDecoration: 'underline',
                  textUnderlineOffset: 6,
                  fontWeight: 600,
                  color: 'inherit',
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                  },
                },
              }}
            >
              <Link
                href={'/reset-password?' + CALLBACK_URL_KEY + '=' + encodeURIComponent(callbackUrl || '')}
                onNavigate={(e) => {
                  if (goToForgotPasswordPage) {
                    e.preventDefault()
                    goToForgotPasswordPage()
                  }
                }}
              >
                {t('Tôi không thể đăng nhập')}
              </Link>
            </Box>
          </Stack>
        </Grid>

        <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} loading={isPending}>
          {t('Đăng nhập tài khoản')}
        </ButtonRoundStyle>
      </Grid>
    </Form>
  )
}
const OTPLoginForm = ({
  onOtpSuccess,
  goToForgotPasswordPage,
}: {
  onOtpSuccess?: (phone: string) => void
  goToForgotPasswordPage?: () => void
}) => {
  const [isPending, startTransition] = useTransition()
  const t = useTranslations('UserProfile')
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get(CALLBACK_URL_KEY)
  // Form cho gửi OTP
  const otpRequestMethod = useForm<yup.InferType<typeof otpRequestSchema>>({
    resolver: yupResolver(otpRequestSchema),
  })

  // Gửi mã OTP
  const onRequestOtp = async (values: yup.InferType<typeof otpRequestSchema>) => {
    startTransition(async () => {
      try {
        const result = await RequestOtpAction(values)

        if (result?.status === 'error') {
          toast.error(result?.message || t('Gửi mã OTP thất bại'))
          return
        }

        // Chuyển sang step OTP với số điện thoại
        toast.success(result?.message || t('Mã OTP đã được gửi thành công!'))
        onOtpSuccess?.(values.phone)
      } catch (error) {
        console.error('Request OTP error:', error)
        toast.error(t('Có lỗi xảy ra khi gửi mã OTP'))
      }
    })
  }

  return (
    <Form onSubmit={otpRequestMethod.handleSubmit(onRequestOtp)} methods={otpRequestMethod}>
      <Stack spacing={1.5}>
        <RHFTextField
          autoFocus
          type="number"
          name="phone"
          label={t('Số điện thoại')}
          placeholder={t('Nhập số điện thoại của bạn')}
          disabled={isPending}
        />
      </Stack>
      <Grid container spacing={1} mt={1}>
        <Grid size={12}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <CheckboxCT name="rememberMe" label={t('Duy trì đăng nhập')} disabled={isPending} />
            <Box
              sx={{
                textAlign: { xs: 'start', sm: 'end' },
                fontSize: '0.875rem',
                '& a': {
                  textDecoration: 'underline',
                  textUnderlineOffset: 6,
                  fontWeight: 600,
                  color: 'inherit',
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                  },
                },
              }}
            >
              <Link
                href={'/reset-password?' + CALLBACK_URL_KEY + '=' + encodeURIComponent(callbackUrl || '')}
                onNavigate={(e) => {
                  if (goToForgotPasswordPage) {
                    e.preventDefault()
                    goToForgotPasswordPage()
                  }
                }}
              >
                {t('Tôi không thể đăng nhập')}
              </Link>
            </Box>
          </Stack>
        </Grid>

        <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} loading={isPending}>
          {t('Đăng nhập bằng OTP')}
        </ButtonRoundStyle>
      </Grid>
    </Form>
  )
}
